import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lightbulb, MegaphoneSimple, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

const ROLES = [
  { value: "influencer", label: "Influencer", icon: MegaphoneSimple, desc: "Upload and get approved" },
  { value: "brand", label: "Brand", icon: Storefront, desc: "Review and approve content" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "influencer", company: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.name}`);
      navigate(`/app/${u.role === "agency_admin" ? "agency" : u.role}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-50 bg-grid flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 bg-yellow-400 rounded-lg flex items-center justify-center">
            <Lightbulb weight="fill" size={20} className="text-black" />
          </div>
          <span className="font-heading font-black text-xl">Mastra Media</span>
        </Link>

        <div className="backdrop-blur-xl bg-zinc-900/70 border border-white/10 rounded-2xl p-8">
          <h1 className="font-heading font-black text-3xl mb-2">Create account</h1>
          <p className="text-zinc-400 text-sm mb-6">Join Mastra Media's workspace</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-300 mb-2 block">I am a</Label>
              <div className="grid grid-cols-2 gap-3">
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  const active = form.role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      data-testid={`register-role-${r.value}`}
                      onClick={() => update("role", r.value)}
                      className={`p-4 rounded-xl border text-left transition-colors ${
                        active
                          ? "bg-yellow-400/10 border-yellow-400 text-yellow-300"
                          : "bg-black border-white/10 text-zinc-300 hover:border-white/20"
                      }`}
                    >
                      <Icon size={22} weight="duotone" className="mb-2" />
                      <div className="font-heading font-bold text-base">{r.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{r.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <GoogleAuthButton
              intendedRole={form.role === "agency_admin" ? "influencer" : form.role}
              testid="register-google-button"
              label={`Continue with Google as ${form.role === "brand" ? "Brand" : "Influencer"}`}
            />

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#0a0a0a] px-3 text-zinc-500 font-mono uppercase tracking-wider">or with email</span>
              </div>
            </div>

            <div>
              <Label htmlFor="name" className="text-zinc-300">Full name</Label>
              <Input
                id="name" data-testid="register-name-input" required
                value={form.name} onChange={(e) => update("name", e.target.value)}
                className="bg-black border-white/10 mt-1.5 h-11" placeholder="Jane Creator"
              />
            </div>

            {form.role === "brand" && (
              <div>
                <Label htmlFor="company" className="text-zinc-300">Brand / Company</Label>
                <Input
                  id="company" data-testid="register-company-input"
                  value={form.company} onChange={(e) => update("company", e.target.value)}
                  className="bg-black border-white/10 mt-1.5 h-11" placeholder="Acme Co."
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email" type="email" data-testid="register-email-input" required
                value={form.email} onChange={(e) => update("email", e.target.value)}
                className="bg-black border-white/10 mt-1.5 h-11" placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <Input
                id="password" type="password" data-testid="register-password-input" required minLength={6}
                value={form.password} onChange={(e) => update("password", e.target.value)}
                className="bg-black border-white/10 mt-1.5 h-11" placeholder="At least 6 characters"
              />
            </div>
            {error && (
              <div data-testid="register-error" className="text-rose-400 text-sm bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit" disabled={loading} data-testid="register-submit-button"
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11 rounded-lg"
            >
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-zinc-400 text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-yellow-400 hover:text-yellow-300 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
